<template>
	<div
		class="bento-tile bento-tile--span-2 quotas-tile"
		data-test="quotas-tile"
	>
		<div class="bento-tile__header">
			<span class="bento-tile__eyebrow">Usage this period</span>
			<v-btn
				variant="text"
				size="small"
				to="/usage"
				class="bento-tile__action"
			>
				View usage
				<ArrowRight16 />
			</v-btn>
		</div>
		<ErrorEmptyState
			v-if="usageUnavailable"
			compact
			title="Could not load your Usage Data"
			message="Often this is a temporary network or connectivity issue."
			:retrying="retryingLoad"
			@retry="retryLoad"
		/>
		<template v-else>
			<div
				v-if="resetLabel || unlimitedQuotas.length"
				class="quotas-tile__meta"
			>
				<div
					v-if="resetLabel"
					class="quotas-tile__reset sgnl-text-color-secondary sgnl-caption-regular"
					data-test="quotas-tile-reset"
				>
					Resets
					<span class="quotas-tile__reset-strong sgnl-text-color-brand sgnl-caption-medium">{{
						resetLabel
					}}</span>
				</div>
				<div
					v-if="unlimitedQuotas.length"
					class="quotas-tile__unlimited-row sgnl-text-color-brand sgnl-paragraph-s-regular"
					data-test="quotas-tile-unlimited"
				>
					<span class="quotas-tile__unlimited-label">Unlimited:</span>
					<span class="quotas-tile__unlimited-items sgnl-text-color-default">{{
						unlimitedQuotas.map((q) => q.label).join(" · ")
					}}</span>
				</div>
			</div>
			<div class="quotas-tile__grid">
				<div
					v-for="q in limitedQuotas"
					:key="q.id"
					class="quotas-tile__row"
					:data-test="`quotas-tile-row-${q.id}`"
				>
					<div class="quotas-tile__line sgnl-text-color-default sgnl-paragraph-s-regular">
						<span>{{ q.label }}</span>
						<span class="quotas-tile__count quotas-tile__count--full sgnl-text-color-secondary">
							{{ q.usedLabel }}
							<span class="quotas-tile__count-max sgnl-text-color-tertiary">/ {{ q.limitLabel }}</span>
						</span>
						<span class="quotas-tile__count quotas-tile__count--compact sgnl-text-color-secondary">
							{{ q.usedLabelCompact }}
							<span class="quotas-tile__count-max sgnl-text-color-tertiary"
								>/ {{ q.limitLabelCompact }}</span
							>
						</span>
					</div>
					<div class="quotas-tile__bar">
						<div
							class="quotas-tile__bar-fill"
							:style="{ width: barWidth(q.pct), background: barColor(q.pct) }"
						/>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import moment from "moment";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";

import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import { mapState } from "pinia";
import { useUserStore, type MonthlyQuotaRow } from "@/stores/user";
import {
	MINIMUM_RETRY_FEEDBACK_MS,
	toStringWithMagnitude,
	toStringWithSIMagnitude,
	withMinimumDuration,
} from "@/js/utilities";

type QuotaRow = MonthlyQuotaRow & {
	usedLabel: string;
	limitLabel: string;
	usedLabelCompact: string;
	limitLabelCompact: string;
};

const formatCount = (n: number): string => {
	if (n >= 1_000_000) return toStringWithMagnitude(n);
	return n.toLocaleString();
};

const formatCountCompact = (n: number): string => {
	if (n >= 1000) return toStringWithSIMagnitude(n, "");
	return n.toLocaleString();
};

const formatLimit = (q: MonthlyQuotaRow, compact: boolean): string => {
	if (q.unlimited) return "Unlimited";
	if (q.limit === null) return "—";
	return compact ? formatCountCompact(Number(q.limit)) : formatCount(Number(q.limit));
};

export default defineComponent({
	name: "QuotasTile",
	components: { ArrowRight16, ErrorEmptyState },
	data: () => ({
		retryingLoad: false,
	}),
	setup() {
		return { userStore: useUserStore() };
	},
	computed: {
		...mapState(useUserStore, {
			monthlyQuotaUsage: "monthlyQuotaUsage",
			resourceUsage: "currentUserResourceUsage",
		}),
		usageUnavailable(): boolean {
			return this.userStore.tierResolutionFailed;
		},
		quotas(): QuotaRow[] {
			return (this.monthlyQuotaUsage as MonthlyQuotaRow[]).map((q) => ({
				...q,
				usedLabel: formatCount(q.used),
				limitLabel: formatLimit(q, false),
				usedLabelCompact: formatCountCompact(q.used),
				limitLabelCompact: formatLimit(q, true),
			}));
		},
		limitedQuotas(): QuotaRow[] {
			return this.quotas.filter((q) => !q.unlimited);
		},
		unlimitedQuotas(): QuotaRow[] {
			return this.quotas.filter((q) => q.unlimited);
		},
		resetLabel(): string {
			const ts = this.resourceUsage?.ResetsAt;
			if (!ts) return "";
			const resetMoment = moment(ts * 1000);
			if (!resetMoment.isValid()) return "";
			const days = Math.max(0, Math.ceil(resetMoment.diff(moment(), "days", true)));
			return `${resetMoment.format("MMM D")} · ${days} day${days === 1 ? "" : "s"}`;
		},
	},
	methods: {
		async retryLoad(): Promise<void> {
			this.retryingLoad = true;
			try {
				await withMinimumDuration(this.reloadQuotaSources(), MINIMUM_RETRY_FEEDBACK_MS);
			} finally {
				this.retryingLoad = false;
			}
		},
		async reloadQuotaSources(): Promise<void> {
			await this.userStore.getCurrentUserTierDetails();
		},
		barWidth(pct: number): string {
			return `${Math.max(2, Math.min(100, pct * 100))}%`;
		},
		barColor(pct: number): string {
			if (pct >= 0.8) return "var(--sgnl-alert-error-border)";
			if (pct >= 0.5) return "var(--sgnl-alert-warning-border)";
			return "var(--sgnl-brand-background)";
		},
	},
});
</script>

<style lang="scss" scoped>
.quotas-tile__meta {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.quotas-tile__grid {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-16);
}

.quotas-tile__unlimited-row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: var(--sgnl-space-6);
	padding: var(--sgnl-space-6) var(--sgnl-space-10);
	border: var(--sgnl-border-width-1) solid var(--sgnl-brand-border);
	border-radius: var(--sgnl-border-radius-6);
}

.quotas-tile__unlimited-label {
	font-weight: 500;
}

.quotas-tile__row {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}

.quotas-tile__line {
	display: flex;
	justify-content: space-between;
}

.quotas-tile__count {
	font-family: var(--sgnl-font-family-mono);
}

.quotas-tile__count--compact {
	display: none;
}

@media (max-width: 424px) {
	.quotas-tile__count--full {
		display: none;
	}
	.quotas-tile__count--compact {
		display: inline;
	}
}

.quotas-tile__bar {
	height: var(--sgnl-unit-4);
	background: var(--sgnl-default-secondary-background);
	border-radius: var(--sgnl-border-radius-6);
	overflow: hidden;
}

.quotas-tile__bar-fill {
	height: 100%;
}
</style>
